import React, { useState, useEffect, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Card, CardHeader, CardBody, Spinner, Alert } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
  coral: '#ff7f50',
  tangerine: '#ff8c42',
  peach: '#ffab73',
};

const dayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DetectionsByDayHour = ({ data = [] }) => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // PROCESS DATA WHEN RECEIVED
  // ============================================
  useEffect(() => {
    console.log("🕐 DetectionsByDayHour received data:", data);
    console.log("📊 Data length:", data?.length);

    if (!data || data.length === 0) {
      console.log("⚠️ No day/hour data available");
      setLoading(false);
      return;
    }

    // Initialize matrix for all days and hours
    const matrix = {
      Sun: Array(24).fill(0),
      Mon: Array(24).fill(0),
      Tue: Array(24).fill(0),
      Wed: Array(24).fill(0),
      Thu: Array(24).fill(0),
      Fri: Array(24).fill(0),
      Sat: Array(24).fill(0)
    };

    // Check data format
    const isAggregated = data[0] && 
      typeof data[0].day !== 'undefined' && 
      typeof data[0].hour !== 'undefined' && 
      typeof data[0].count !== 'undefined';

    console.log("📊 Data format:", isAggregated ? "Aggregated" : "Raw");

    if (isAggregated) {
      // Process aggregated data from analytics endpoint
      data.forEach((item) => {
        const dayIndex = parseInt(item.day);
        const hour = parseInt(item.hour);
        const count = parseInt(item.count) || 0;

        if (dayIndex >= 0 && dayIndex <= 6 && hour >= 0 && hour <= 23) {
          const dayName = dayMap[dayIndex];
          matrix[dayName][hour] = count;
        }
      });
      console.log("✅ Processed aggregated data");
    } else {
      // Process raw threat data (fallback)
      data.forEach((item) => {
        const ts = item.timestamp;
        if (!ts) return;

        const dateObj = new Date(ts);
        const dayIndex = dateObj.getDay();
        const hour = dateObj.getHours();

        if (dayIndex >= 0 && dayIndex <= 6 && hour >= 0 && hour <= 23) {
          const dayName = dayMap[dayIndex];
          matrix[dayName][hour] += 1;
        }
      });
      console.log("✅ Processed raw threat data");
    }

    // Calculate max value for reference
    const allValues = Object.values(matrix).flat();
    const maxValue = Math.max(...allValues);
    console.log("📊 Max detections in any hour:", maxValue);

    // Format for ApexCharts heatmap
    const formattedSeries = dayMap.map((day) => ({
      name: day,
      data: matrix[day].map((count, hour) => ({
        x: `${hour}:00`,
        y: count
      }))
    }));

    console.log("📊 Formatted series for chart");
    setSeries(formattedSeries);
    setLoading(false);
  }, [data]);

  // ============================================
  // CHART OPTIONS - ONLY ORANGE COLOR VARIATIONS
  // ============================================
  const options = useMemo(() => ({
    chart: {
      height: 350,
      type: "heatmap",
      toolbar: { 
        show: true,
        tools: {
          download: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      fontFamily: 'Poppins, sans-serif'
    },

    plotOptions: {
      heatmap: {
        radius: 4,
        enableShades: false,
        distributed: false,
        colorScale: {
          ranges: [
            // Very mild orange for no detections
            { from: 0, to: 0, color: 'rgba(255, 107, 53, 0.05)', name: "No detections" },
            { from: 1, to: 5, color: ORANGE_COLORS.subtle, name: "1-5" },
            { from: 6, to: 10, color: ORANGE_COLORS.muted, name: "6-10" },
            { from: 11, to: 20, color: ORANGE_COLORS.lighter, name: "11-20" },
            { from: 21, to: 50, color: ORANGE_COLORS.peach, name: "21-50" },
            { from: 51, to: 100, color: ORANGE_COLORS.light, name: "51-100" },
            { from: 101, to: 200, color: ORANGE_COLORS.tangerine, name: "101-200" },
            { from: 201, to: 500, color: ORANGE_COLORS.coral, name: "201-500" },
            { from: 501, to: 1000, color: ORANGE_COLORS.primary, name: "501-1000" },
            { from: 1001, to: 2000, color: ORANGE_COLORS.dark, name: "1001-2000" },
            { from: 2001, to: 10000, color: ORANGE_COLORS.darker, name: "2000+" }
          ]
        }
      }
    },

    dataLabels: { 
      enabled: false 
    },

    stroke: {
      width: 1,
      colors: ['rgba(255, 255, 255, 0.2)']
    },

    xaxis: {
      type: "category",
      title: { 
        text: "Hour of Day",
        style: {
          color: '#858d98',
          fontSize: '12px',
          fontWeight: 500
        }
      },
      labels: { 
        rotate: -45,
        rotateAlways: true,
        style: { 
          fontSize: "10px",
          colors: '#858d98'
        }
      },
      axisBorder: {
        show: true,
        color: 'rgba(133, 141, 152, 0.2)'
      }
    },

    yaxis: { 
      title: { 
        text: "Day of Week",
        style: {
          color: '#858d98',
          fontSize: '12px',
          fontWeight: 500
        }
      },
      labels: {
        style: {
          colors: '#858d98',
          fontSize: '11px',
          fontWeight: 500
        }
      }
    },

    grid: { 
      padding: { 
        right: 20, 
        left: 10 
      },
      borderColor: 'rgba(133, 141, 152, 0.1)'
    },

    legend: { 
      show: false // Hide legend to make chart cleaner
    },

    tooltip: {
      y: {
        formatter: function(value) {
          if (!value || value === 0) return "No detections";
          return value + " detection" + (value !== 1 ? "s" : "");
        }
      }
    }
  }), []); // Empty dependency - options don't need to change

  // ============================================
  // RENDER
  // ============================================
  return (
    <Card 
      className="h-100"
      style={{ 
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}
    >
      <CardHeader className="d-flex align-items-center justify-content-between" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <h4 className="card-title mb-0 flex-grow-1">
          <span style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
            background: toRgba(ORANGE_COLORS.primary, 0.10),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
            flexShrink: 0
          }}>
            <FeatherIcon icon="calendar" size={18} style={{ color: ORANGE_COLORS.primary, lineHeight: 1 }} />
          </span>
          Detections by Day & Hour
        </h4>
      </CardHeader>

      <CardBody>
        {loading ? (
          <div className="text-center py-5">
            <Spinner style={{ color: ORANGE_COLORS.primary }} />
            <p className="mt-2 text-muted small">Loading heatmap data...</p>
          </div>
        ) : series.length === 0 ? (
          <Alert 
            style={{
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              color: '#856404',
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}
          >
            <FeatherIcon icon="info" size={16} className="me-2" />
            No detection patterns available
          </Alert>
        ) : (
          <>
            <ReactApexChart
              options={options}
              series={series}
              type="heatmap"
              height={350}
            />
            <div className="mt-3 pt-3 border-top text-center">
              <span className="text-muted small">
                <FeatherIcon icon="clock" size={14} className="me-1" style={{ color: ORANGE_COLORS.primary }} />
                Showing detection patterns across 7 days and 24 hours
              </span>
              <div className="mt-2">
                <span className="text-muted small">
                  Lighter shades = fewer detections • Darker shades = more detections
                </span>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default DetectionsByDayHour;
